import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormFieldError } from '@/components/ui/form-field-error';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/toast';
import { useChangePassword } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api-errors';

import { messageFor } from '@/pages/auth/validation-message';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'currentPasswordRequired'),
    newPassword: z.string().min(12, 'passwordTooShort'),
    confirmPassword: z.string().min(1, 'confirmPasswordRequired'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'passwordMismatch',
    path: ['confirmPassword'],
  });

type Values = z.infer<typeof schema>;

export const PasswordSection = () => {
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const changePassword = useChangePassword();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.reset();
      toast.success(t('profile.passwordSaved'));
    } catch (err) {
      if (err instanceof ApiError) {
        form.setError('root', { message: tErrors(err.code, err.params) });
      }
    }
  });

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>{t('profile.passwordTitle')}</CardTitle>
        <CardDescription>{t('profile.passwordDescription')}</CardDescription>
      </CardHeader>

      <form
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        <CardContent className="space-y-4">
          <FormField
            label={t('field.currentPassword')}
            required
            error={messageFor(form.formState.errors.currentPassword?.message, t)}
          >
            {(field) => (
              <PasswordInput
                {...field}
                {...form.register('currentPassword')}
                autoComplete="current-password"
              />
            )}
          </FormField>

          <FormField
            label={t('field.newPassword')}
            required
            tipId="auth.password"
            error={messageFor(form.formState.errors.newPassword?.message, t)}
          >
            {(field) => (
              <PasswordInput
                {...field}
                {...form.register('newPassword')}
                autoComplete="new-password"
              />
            )}
          </FormField>

          <FormField
            label={t('field.confirmPassword')}
            required
            error={messageFor(form.formState.errors.confirmPassword?.message, t)}
          >
            {(field) => (
              <PasswordInput
                {...field}
                {...form.register('confirmPassword')}
                autoComplete="new-password"
              />
            )}
          </FormField>

          <FormFieldError message={form.formState.errors.root?.message} />
        </CardContent>

        <CardFooter className="flex justify-end">
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('profile.passwordSave')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};
